import * as React from "react";
import {
  useGetBusinessReportOutcomeQuery,
  useGetFamilySourceOutcomesQuery,
  useGetSnapshotEventsQuery,
  useUpdateBusinessReportOutcomeResultMutation,
} from "@/modules/snapshots/businessReports.api";
import { EvidenceWithContext } from "@/modules/snapshots/outcome-details/types";
import {
  BusinessReportOutcome,
  BusinessReportOutcomeEvidence,
  BusinessReportOutcomeEvidenceResult,
  BusinessReportOutcomeResult,
  canEditOutcomeResults,
} from "@/modules/snapshots/businessReports.types";
import { Highlight } from "@/types/evidences";
import { Loader } from "@/components/ui/loader";
import { MarkdownRenderer } from "@/components/feature/markdown/markdownRenderer";
import { reformatCitations } from "@/modules/chat/chat.utils";
import { Button } from "@/components/ui/button";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { addBusinessReportOutcomeResult, removeBusinessReportOutcomeResult } from "@/store/slices/snapshotCompareSlice";
import { GridSidebarTab } from "@/modules/snapshots/grid/sidebar/tabs";
import { useToggle } from "react-use";
import { Link, useNavigate } from "react-router";
import { ModifySnapshotEventDialog } from "@/modules/snapshots/snapshotEvents/modifySnapshotEventDialog";
import { EventsDropdown } from "@/modules/snapshots/outcome-details/businessReportOutcomeRow/EventsDropdown";
import * as analytics from "@/services/analytics";
import { parseISO } from "date-fns";
import { Textarea } from "@/components/ui/textarea";
import { useGetDirectoryUserQuery } from "@/api/users-admin";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { MoreHorizontal, Pencil, Undo2, MessageSquareIcon } from "lucide-react";
import { EventDateAlert } from "@/modules/snapshots/outcome-details/components/EventDateAlert";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { GridSidebarReviewSection } from "@/modules/snapshots/grid/sidebar/reviewSection";
import { ResultDetailsSection } from "@/modules/snapshots/resultDetails/ResultDetailsSection";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  getInitialSourceFseId,
  isEvidenceForPrompt,
  toSpreadsheetTarget,
} from "@/modules/snapshots/grid/sidebar/sourceDocument";
import { getTypedSnapshotCellDisplayText } from "@/modules/snapshots/grid/cellText";
import { getResultUpdateErrorMessage } from "@/modules/snapshots/resultUpdateErrors";
import { citationEvidencesFor, outcomeEvidences } from "@/modules/snapshots/outcome-details/outcomeEvidences";

interface Props {
  businessReportOutcome: BusinessReportOutcome;
  result: BusinessReportOutcomeResult;
  setTab: (tab: GridSidebarTab) => void;
  snapshotCollectionId: string;
  isCitationView?: boolean;
  /** Hides the edit affordance for rows the current context must not rewrite. */
  canEdit?: boolean;
  /**
   * Alternative writer for the edited value. The contract family pipeline saves through its own
   * staged-override endpoint so the review audit trail stays the single source of truth.
   */
  onSaveResult?: (value: string) => Promise<void>;
  /** Restores the generated value, when the hosting context keeps an immutable original. */
  onRevertResult?: () => Promise<void>;
}

export const GridSidebarSnapshotResult: React.FC<Props> = ({
  result,
  businessReportOutcome,
  setTab,
  snapshotCollectionId,
  isCitationView = false,
  canEdit = true,
  onSaveResult,
  onRevertResult,
}) => {
  const { data: businessReportOutcomeDetails = null, isLoading } = useGetBusinessReportOutcomeQuery(
    businessReportOutcome.id,
  );
  const [updateResult, { isLoading: isUpdating }] = useUpdateBusinessReportOutcomeResultMutation();
  const [isCreateSnapshotEventDialogOpen, toggleIsCreateSnapshotEventDialogOpen] = useToggle(false);
  const [isEditMode, toggleIsEditMode] = useToggle(false);
  const getEditableContent = (r: BusinessReportOutcomeResult) => (r.is_date && r.date ? r.date : r.result);
  const [editedContent, setEditedContent] = React.useState(getEditableContent(result));
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const compareResults = useAppSelector(state => state.snapshotCompare.businessReportOutcomeResults);
  const isInCompareList = result.id in compareResults.entities;

  const { data: events = [] } = useGetSnapshotEventsQuery({
    businessReportOutcomeId: businessReportOutcome.id,
    businessReportOutcomeResultId: result.id,
  });

  const { data: editedByUser } = useGetDirectoryUserQuery(result.edited_by_id ?? "", {
    skip: !result.edited_by_id,
  });
  const { currentPdfId, setCurrentPdfId } = useCurrentPdf();
  const { formatDateTime } = useDateFormatter();

  const currentResult = React.useMemo(() => {
    if (businessReportOutcomeDetails) {
      const found = businessReportOutcomeDetails.result.find(r => r.id === result.id);
      if (found) return found;
    }
    return result;
  }, [businessReportOutcomeDetails, result]);

  // Depend on the displayed string, not the result object, so a refetch that returns an equal
  // value does not discard in-progress edits.
  const currentEditableContent = getEditableContent(currentResult);
  React.useEffect(() => {
    setEditedContent(currentEditableContent);
  }, [currentEditableContent]);

  const handleViewEvent = (eventId: string) => {
    navigate(
      `/snapshot-events?businessReportOutcomeId=${businessReportOutcome.id}&businessReportOutcomeResultId=${result.id}&documentName=&eventId=${eventId}`,
    );
  };

  const handleCreateEvent = () => {
    analytics.snapshotCreateEvent();
    toggleIsCreateSnapshotEventDialogOpen();
  };

  const handleToggleCompare = () => {
    if (isInCompareList) {
      dispatch(removeBusinessReportOutcomeResult(result.id));
      toast.success("Removed from compare list");
    } else {
      analytics.snapshotCompare();
      setTab("comparison");
      dispatch(
        addBusinessReportOutcomeResult({
          ...result,
          documentName: businessReportOutcome.fs_entry?.document_name || "",
        }),
      );
      toast.success("Added to compare list");
    }
  };

  const handleStartEdit = () => {
    setEditedContent(getEditableContent(currentResult));
    toggleIsEditMode();
  };

  const handleCancelEdit = () => {
    setEditedContent(getEditableContent(currentResult));
    toggleIsEditMode();
  };

  const [isSavingOverride, setIsSavingOverride] = React.useState(false);

  const handleSaveEdit = async () => {
    try {
      if (onSaveResult) {
        setIsSavingOverride(true);
        await onSaveResult(editedContent);
      } else {
        await updateResult({
          id: businessReportOutcome.id,
          resultId: result.id,
          data: { result: editedContent },
        }).unwrap();
      }
      toast.success("Response updated successfully");
      toggleIsEditMode();
    } catch (error) {
      toast.error(getResultUpdateErrorMessage(error, "Failed to update response"));
    } finally {
      setIsSavingOverride(false);
    }
  };

  const isSaving = isUpdating || isSavingOverride;

  const handleRevert = async () => {
    if (!onRevertResult) return;
    try {
      setIsSavingOverride(true);
      await onRevertResult();
      toast.success("Generated value restored");
    } catch {
      toast.error("Failed to restore the generated value");
    } finally {
      setIsSavingOverride(false);
    }
  };

  const ownEvidences = React.useMemo(
    () =>
      !businessReportOutcomeDetails
        ? []
        : businessReportOutcomeDetails.evidences.reduce(
            (acc: EvidenceWithContext[], evidence: BusinessReportOutcomeEvidence) => [
              ...acc,
              ...(isEvidenceForPrompt(evidence.business_report_prompt_id, currentResult.business_report_prompt_id)
                ? evidence.result.map((row: BusinessReportOutcomeEvidenceResult) => ({
                    highlights: row.highlights ? (JSON.parse(row.highlights) as Highlight[]) : [],
                    citationId: row.citation_id,
                    documentId: row.document_id,
                    fseId: row.fse_id,
                    business_report_prompt_id: evidence.business_report_prompt_id,
                    id: `evidence-${row.citation_id}`,
                    answerOffsetEnd: 0,
                    answerOffsetStart: 0,
                    spreadsheetTarget: toSpreadsheetTarget(row.spreadsheet_target),
                    sourceDocumentContentType: businessReportOutcome.fs_entry?.content_type ?? null,
                  }))
                : []),
            ],
            [],
          ),
    [
      businessReportOutcome.fs_entry?.content_type,
      businessReportOutcomeDetails,
      currentResult.business_report_prompt_id,
    ],
  );
  // A family summary row has no document of its own. A one-document family copies that document's
  // cells, citation markers included, so they cite its evidence; a consolidated cell cites through
  // its snapshot citations.
  const isFamilyRow = Boolean(businessReportOutcome.summary_row_kind);
  const { data: familySourceOutcomes } = useGetFamilySourceOutcomesQuery(businessReportOutcome.id, {
    skip: !isFamilyRow,
  });
  const evidences = React.useMemo(
    () =>
      isFamilyRow
        ? citationEvidencesFor(
            ownEvidences,
            (familySourceOutcomes ?? []).map(outcome => ({
              outcome,
              evidences: outcomeEvidences(outcome).filter(evidence =>
                isEvidenceForPrompt(evidence.business_report_prompt_id, currentResult.business_report_prompt_id),
              ),
            })),
          )
        : ownEvidences,
    [currentResult.business_report_prompt_id, familySourceOutcomes, isFamilyRow, ownEvidences],
  );
  React.useEffect(() => {
    if (currentPdfId) return;
    const initialSourceFseId = getInitialSourceFseId(businessReportOutcome.fs_entry?.id, evidences);
    if (initialSourceFseId) setCurrentPdfId(initialSourceFseId);
  }, [businessReportOutcome.fs_entry?.id, currentPdfId, evidences, setCurrentPdfId]);
  const editorName = editedByUser ? `${editedByUser.name} ${editedByUser.surname}`.trim() : null;

  return (
    <>
      <div className="flex h-full flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          <div className="my-3 px-6">
            {isLoading ? (
              <Loader />
            ) : isEditMode ? (
              <Textarea
                aria-label="Response content"
                value={editedContent}
                onChange={e => setEditedContent(e.target.value)}
                className="min-h-[300px] w-full font-mono text-sm"
                placeholder="Enter response content..."
              />
            ) : (
              <>
                {currentResult.data_type === "relevancy" && currentResult.relevancy != null && (
                  <p>{getTypedSnapshotCellDisplayText(currentResult)}</p>
                )}
                <MarkdownRenderer
                  content={reformatCitations(currentResult.result)}
                  evidences={evidences}
                  processCitations
                  snapshotCitations={
                    businessReportOutcomeDetails?.result.find(item => item.id === result.id)?.snapshot_citations
                  }
                  webSearchSources={
                    businessReportOutcomeDetails?.result.find(item => item.id === result.id)?.web_search_sources
                      ?.sources ?? []
                  }
                />
                {currentResult.is_date && currentResult.date && <EventDateAlert date={currentResult.date} />}
                <ResultDetailsSection
                  details={businessReportOutcomeDetails?.result.find(item => item.id === result.id)?.result_details}
                />
                {currentResult.review?.verdict && (
                  <GridSidebarReviewSection review={currentResult.review} onOpenComparison={() => setTab("review")} />
                )}
                {currentResult.edited_at && (
                  <div className="mt-4 border-t pt-3 text-xs text-gray-500">
                    Last edited{editorName ? ` by ${editorName}` : ""} on {formatDateTime(currentResult.edited_at)}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex justify-between gap-1 border-t p-3 pb-5">
          {isEditMode ? (
            <>
              <Button variant="second-white" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="default" size="sm" onClick={handleSaveEdit} disabled={isSaving}>
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </>
          ) : (
            <>
              {!isCitationView && (
                <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={handleToggleCompare}>
                  {isInCompareList ? "Remove from Compare" : "Compare"}
                </Button>
              )}
              <div className="ml-auto flex gap-1">
                {businessReportOutcome.fs_entry && (
                  <Button variant="outline" size="sm" className="h-8 gap-1.5" asChild>
                    <Link
                      to={`/chat/${businessReportOutcome.fs_entry.id}`}
                      state={{
                        fromSnapshot: `/snapshots/grid/${businessReportOutcome.business_report_id}/${snapshotCollectionId}?outcomeId=${businessReportOutcome.id}&resultId=${result.id}`,
                      }}
                    >
                      <MessageSquareIcon className="mr-1 h-4 w-4" />
                      Q&A
                    </Link>
                  </Button>
                )}
                {events.length > 0 && <EventsDropdown events={events} onViewEvent={handleViewEvent} />}
                {!isCitationView && (
                  <Button variant="outline" size="sm" className="h-8 gap-1.5" onClick={handleCreateEvent}>
                    Create event
                  </Button>
                )}
                {/* The API rejects edits to a superseded family version, whichever view opened the drawer. */}
                {canEdit && canEditOutcomeResults(businessReportOutcome) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8 w-8 p-0" aria-label="Response actions">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={handleStartEdit}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      {onRevertResult && (
                        <DropdownMenuItem onClick={handleRevert} disabled={isSaving}>
                          <Undo2 className="mr-2 h-4 w-4" />
                          Revert to generated
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {isCreateSnapshotEventDialogOpen && (
        <ModifySnapshotEventDialog
          mode="create"
          eventDate={result.date ? parseISO(result.date) : undefined}
          open={isCreateSnapshotEventDialogOpen}
          onClose={toggleIsCreateSnapshotEventDialogOpen}
          eventType={result.name}
          businessReportOutcomeId={businessReportOutcome.id}
          businessReportOutcomeResultId={result.id}
        />
      )}
    </>
  );
};
